/* eslint-disable jsx-a11y/media-has-caption */
import { useVideoBySlug, useVideosByCategory } from '@bpm-web-app/swr-hooks';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Analytics, getCurrentPlatformLink, sortArray } from '@bpm-web-app/utils';
import { useMemo, useRef } from 'react';
import styles from './video-by-slug.module.css';
import { CardCarousel, useActionModal } from '../../shared';
import { membershipRequiredActionModalProps } from '../../shared/action-modal/action-modal';
import { VideoCard } from '../../shared/card/video-card/video-card';
import { usePlayer } from '../../player-context';
import { TrackListSupreme } from '../../shared/track-list/supreme/track-list-supreme';
import { LocalSortingKeys, sortMediaLocally, useLocalSort } from '../../sort-options-sheet/sort-options-sheet';
import { TrackListPresetSupreme } from '../../shared/track-list/supreme/track-list-supreme-helpers';

export interface VideoBySlugProps {
    slug: string;
}

export function VideoBySlug({ slug }: VideoBySlugProps) {
    const { data: video, isLoading, mutate } = useVideoBySlug(slug);
    const { data: videosForCategory } = useVideosByCategory(video?.category?.slug);
    const { openModal, closeModal: closeActionModal } = useActionModal();
    const { onPause } = usePlayer();
    const [sortKey, setSortKey] = useLocalSort();

    const router = useRouter();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const sortedTrackList = useMemo(() => {
        return sortMediaLocally((video)?.albums || [], sortKey);
    }, [video, sortKey]);

    const ListComponent = useMemo(() => {
        if (!video) return null;
        if (sortedTrackList.length === 0) return null;
        return (
            <>
                <p className={styles['video-playlist-title']}>Used in this Video</p>
                <div className={styles['video-playlist']}>
                    <TrackListSupreme
                        list={sortedTrackList}
                        preset={TrackListPresetSupreme.Download}
                        isSortable
                        onSort={(nextSort) => setSortKey(nextSort as LocalSortingKeys)}
                        selectedSortType={sortKey}
                        onDownloadRevalidate={(downloadMedia) => {
                            if (downloadMedia) {
                                // eslint-disable-next-line no-param-reassign
                                downloadMedia.download_count += 1;
                            }
                            mutate({ data: video }, false);
                        }}
                    />
                </div>
            </>
        );
    }, [mutate, video, setSortKey, sortKey, sortedTrackList]);

    if (isLoading) {
        return null;
    }

    if (!video) {
        return <div>Video not found</div>;
    }

    const otherVideos = videosForCategory?.filter((v) => v.id !== video.id) || [];

    return (
        <div className={classNames(styles['video-by-slug'], 'spacing__window')}>
            <div className={styles['video-container']}>
                <video
                    ref={videoRef}
                    className={styles['video-player']}
                    src={video.stream_url || video.thumbnail_url}
                    poster={video.thumbnail_url}
                    onClick={() => {
                        if (!video.stream_url) {
                            videoRef.current?.pause();
                            openModal(
                                membershipRequiredActionModalProps({
                                    isCreate: false,
                                    onConfirm: () => {
                                        router.replace(getCurrentPlatformLink('/account/plan'));
                                        closeActionModal();
                                    },
                                    onClose: () => {
                                        closeActionModal();
                                    },
                                })
                            );
                        }
                    }}
                    onPlay={() => {
                        if (!video.stream_url) {
                            videoRef.current?.pause();
                            openModal(
                                membershipRequiredActionModalProps({
                                    isCreate: false,
                                    onConfirm: () => {
                                        router.replace(getCurrentPlatformLink('/account/plan'));
                                        closeActionModal();
                                    },
                                    onClose: () => {
                                        closeActionModal();
                                    },
                                })
                            );
                        } else {
                            onPause();
                            Analytics.trackClick('play_video', video.id.toString(), { video: video.title });
                            Analytics.trackGeneric('play_video', { video_id: video.id.toString() });
                        }
                    }}
                    onEnded={() => {
                        Analytics.trackGeneric('play_video_ended', { video_id: video.id.toString() });
                    }}
                    preload={video.stream_url ? 'auto' : 'none'}
                    controls
                    controlsList="nodownload"
                />
            </div>
            <h1 className={styles['video-title']}>{video.title}</h1>
            <p className={styles['video-description']}>{video.description}</p>

            {ListComponent}

            {otherVideos.length > 0 && (
                <CardCarousel title={`More in ${video.category?.name}`} isInContainer noPadding>
                    {otherVideos.map((set) => (
                        <VideoCard
                            key={set.id}
                            video={set} />
                    ))}
                </CardCarousel>
            )}
        </div>
    );
}

export default VideoBySlug;
/* eslint-enable jsx-a11y/media-has-caption */
